import { useState, useEffect } from 'react'
import { useLocale } from '../contexts/LocaleContext'
import {
  FileText,
  Briefcase,
  Shield,
  Palette,
  Plus,
  Search,
  Trash2,
  Edit3,
  Star,
  Sparkles,
  Copy,
  MoreVertical,
  X,
  Check,
  ArrowLeft,
  User,
  FolderOpen,
  Building2,
  Eye
} from 'lucide-react'
import { Block } from '../components/BlockEditor'
import { proposalTemplates, ProposalTemplate } from '../components/BlockEditor/templates'
import TemplatePreviewModal from '../components/BlockEditor/TemplatePreviewModal'

// Saved Template interface
export interface SavedTemplate {
  id: string
  name: string
  description: string
  blocks: Block[]
  color: string
  icon: string
  coverImage?: string
  createdAt: string
  updatedAt: string
}

// LocalStorage key
const SAVED_TEMPLATES_KEY = 'quor_saved_templates'

// Load saved templates from localStorage
export function loadSavedTemplates(): SavedTemplate[] {
  try {
    const saved = localStorage.getItem(SAVED_TEMPLATES_KEY)
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

// Save templates to localStorage
export function saveSavedTemplates(templates: SavedTemplate[]): void {
  localStorage.setItem(SAVED_TEMPLATES_KEY, JSON.stringify(templates))
}

// Add a new saved template
export function addSavedTemplate(template: Omit<SavedTemplate, 'id' | 'createdAt' | 'updatedAt'>): SavedTemplate {
  const templates = loadSavedTemplates()
  const newTemplate: SavedTemplate = {
    ...template,
    id: `saved-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
  templates.push(newTemplate)
  saveSavedTemplates(templates)
  return newTemplate
}

// Delete a saved template
export function deleteSavedTemplate(id: string): void {
  const templates = loadSavedTemplates()
  saveSavedTemplates(templates.filter(t => t.id !== id))
}

// Update a saved template
export function updateSavedTemplate(id: string, updates: Partial<SavedTemplate>): void {
  const templates = loadSavedTemplates()
  const index = templates.findIndex(t => t.id === id)
  if (index !== -1) {
    templates[index] = { ...templates[index], ...updates, updatedAt: new Date().toISOString() }
    saveSavedTemplates(templates)
  }
}

interface TemplatesPageProps {
  onUseTemplate: (blocks: Block[]) => void
  onBack: () => void
}

const _templateIcons: Record<string, React.ReactNode> = {
  FileText: <FileText className="w-5 h-5" />,
  Briefcase: <Briefcase className="w-5 h-5" />,
  Shield: <Shield className="w-5 h-5" />,
  Palette: <Palette className="w-5 h-5" />,
  Plus: <Plus className="w-5 h-5" />,
  Star: <Star className="w-5 h-5" />,
  User: <User className="w-5 h-5" />
}
void _templateIcons // Available for future use

// Default cover images for saved templates
const defaultCoverImages = [
  'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=400&fit=crop'
]

const colorOptions = [
  { value: 'pink', label: 'Pink', bg: 'bg-slate-500' },
  { value: 'blue', label: 'Blue', bg: 'bg-blue-500' },
  { value: 'emerald', label: 'Teal', bg: 'bg-teal-500' },
  { value: 'purple', label: 'Purple', bg: 'bg-rose-500' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500' },
  { value: 'amber', label: 'Yellow', bg: 'bg-amber-500' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500' }
]

export default function TemplatesPage({ onUseTemplate, onBack }: TemplatesPageProps) {
  const { tx } = useLocale()
  const [activeTab, setActiveTab] = useState<'all' | 'builtin' | 'saved'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [savedTemplates, setSavedTemplates] = useState<SavedTemplate[]>([])
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<SavedTemplate | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [previewTemplate, setPreviewTemplate] = useState<ProposalTemplate | null>(null)

  // Load saved templates on mount
  useEffect(() => {
    setSavedTemplates(loadSavedTemplates())
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-template-menu]')) {
        setActiveMenu(null)
      }
    }
    if (activeMenu) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [activeMenu])

  const handleDeleteSaved = (id: string) => {
    deleteSavedTemplate(id)
    setSavedTemplates(loadSavedTemplates())
    setActiveMenu(null)
  }

  const handleDuplicate = (template: SavedTemplate) => {
    addSavedTemplate({
      name: `${template.name} ${tx('(Copy)', '(עותק)')}`,
      description: template.description,
      blocks: template.blocks,
      color: template.color,
      icon: template.icon,
      coverImage: template.coverImage
    })
    setSavedTemplates(loadSavedTemplates())
    setActiveMenu(null)
  }

  const handleUseTemplate = (blocks: Block[]) => {
    const newBlocks = blocks.map((block, index) => ({
      ...block,
      id: `block-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
      order: index
    }))
    onUseTemplate(newBlocks)
  }

  const handleSaveEdit = () => {
    if (editingTemplate) {
      updateSavedTemplate(editingTemplate.id, {
        name: editingTemplate.name,
        description: editingTemplate.description,
        color: editingTemplate.color
      })
      setSavedTemplates(loadSavedTemplates())
      setEditingTemplate(null)
    }
  }

  // Filter templates
  const filteredBuiltIn = proposalTemplates.filter(t =>
    t.id !== 'empty' &&
    (t.nameHe.includes(searchQuery) || t.descriptionHe.includes(searchQuery)) &&
    (selectedCategory === 'all' || t.category === selectedCategory)
  )

  const filteredSaved = savedTemplates.filter(t =>
    t.name.includes(searchQuery) || t.description.includes(searchQuery)
  )

  const showBuiltIn = activeTab === 'all' || activeTab === 'builtin'
  const showSaved = activeTab === 'all' || activeTab === 'saved'

  return (
    <div className="min-h-screen bg-slate-100" dir="ltr">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">{tx('Template Library', 'ספריית תבניות')}</h1>
                <p className="text-sm text-slate-500">
                  {tx('Choose a template for a quick start', 'בחר תבנית להתחלה מהירה')}
                </p>
              </div>
            </div>

            {/* Search */}
            <div className="relative w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder={tx('Search templates...', 'חפש תבניות...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400 focus:bg-white transition-all"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pb-0 border-t border-slate-100">
          <div className="flex items-center gap-1 -mb-px pt-2">
            {[
              { id: 'all', label: tx('All Templates', 'כל התבניות'), count: filteredBuiltIn.length + filteredSaved.length },
              { id: 'builtin', label: tx('Built-in', 'מובנות'), count: filteredBuiltIn.length },
              { id: 'saved', label: tx('Saved', 'שמורות'), count: filteredSaved.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-slate-500 text-slate-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                {tab.label}
                <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                  activeTab === tab.id ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r border-slate-200 min-h-[calc(100vh-130px)] p-4 sticky top-[130px]">
          <div className="space-y-1">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-slate-50 text-slate-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <FolderOpen className="w-4 h-4" />
              {tx('All Templates', 'כל התבניות')}
            </button>
            <button
              onClick={() => setSelectedCategory('basic')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'basic'
                  ? 'bg-slate-50 text-slate-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              {tx('Basic', 'בסיסי')}
            </button>
            <button
              onClick={() => setSelectedCategory('professional')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'professional'
                  ? 'bg-slate-50 text-slate-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              {tx('Professional', 'מקצועי')}
            </button>
            <button
              onClick={() => setSelectedCategory('creative')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'creative'
                  ? 'bg-slate-50 text-slate-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Palette className="w-4 h-4" />
              {tx('Creative', 'יצירתי')}
            </button>
            <button
              onClick={() => setSelectedCategory('industry')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'industry'
                  ? 'bg-slate-50 text-slate-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Building2 className="w-4 h-4" />
              {tx('Industry', 'תעשייתי')}
            </button>
          </div>

          {/* Saved templates shortcut */}
          {savedTemplates.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">{tx('Saved Templates', 'תבניות שמורות')}</h3>
              <div className="space-y-1">
                {savedTemplates.slice(0, 5).map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleUseTemplate(t.blocks)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors truncate"
                  >
                    <Star className="w-3 h-3 text-amber-500 flex-shrink-0" />
                    <span className="truncate">{t.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {/* Saved Templates Section */}
          {showSaved && filteredSaved.length > 0 && (
            <section className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-slate-800">{tx('My Templates', 'התבניות שלי')}</h2>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                {filteredSaved.map((template, index) => (
                  <div
                    key={template.id}
                    className="group relative rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                    onClick={() => handleUseTemplate(template.blocks)}
                  >
                    {/* Cover Image */}
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={template.coverImage || defaultCoverImages[index % defaultCoverImages.length]}
                        alt={template.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                      {/* Title on Image */}
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h3 className="text-white font-semibold text-sm">{template.name}</h3>
                      </div>

                      {/* Badge */}
                      <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-amber-500/90 rounded text-[10px] font-medium text-white">
                        <Star className="w-2.5 h-2.5" />
                        {tx('Custom', 'מותאם אישית')}
                      </div>

                      {/* Menu Button */}
                      <div className="absolute top-2 left-2" data-template-menu>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setActiveMenu(activeMenu === template.id ? null : template.id)
                          }}
                          className="p-1 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded text-white transition-colors"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {activeMenu === template.id && (
                          <div className="absolute left-0 top-full mt-1 w-32 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditingTemplate(template)
                                setActiveMenu(null)
                              }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                              {tx('Edit', 'עריכה')}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDuplicate(template)
                              }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                            >
                              <Copy className="w-3.5 h-3.5" />
                              {tx('Duplicate', 'שכפל')}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteSaved(template.id)
                              }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {tx('Delete', 'מחק')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="p-2.5 flex items-center justify-between border-t border-slate-100">
                      <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <Star className="w-2.5 h-2.5" />
                        {template.name}
                      </span>
                      <span className="text-[10px] text-slate-400">{template.blocks.length} {tx('blocks', 'בלוקים')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Empty Saved State */}
          {showSaved && filteredSaved.length === 0 && activeTab === 'saved' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center mb-10">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">{tx('No saved templates', 'אין תבניות שמורות')}</h3>
              <p className="text-slate-500 mb-4 max-w-md mx-auto">
                {tx('When you edit a proposal, you can save it as a template for future reuse', 'כשאתה עורך הצעה, תוכל לשמור אותה כתבנית לשימוש עתידי')}
              </p>
              <button
                onClick={() => setActiveTab('builtin')}
                className="px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                {tx('Browse built-in templates', 'עיין בתבניות מובנות')}
              </button>
            </div>
          )}

          {/* Built-in Templates Section */}
          {showBuiltIn && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-slate-500" />
                <h2 className="text-lg font-semibold text-slate-800">{tx('Built-in Templates', 'תבניות מובנות')}</h2>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                {filteredBuiltIn.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleUseTemplate(template.blocks)}
                    className="group relative rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300 text-left"
                  >
                    {/* Cover Image */}
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={template.coverImage}
                        alt={template.nameHe}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                      {/* Title on Image */}
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h3 className="text-white font-semibold text-sm">{template.nameHe}</h3>
                        <p className="text-white/70 text-xs line-clamp-1">{template.descriptionHe}</p>
                      </div>

                      {/* Category Badge */}
                      <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-medium text-white ${
                        template.category === 'professional' ? 'bg-rose-500/90' :
                        template.category === 'creative' ? 'bg-slate-500/90' :
                        template.category === 'industry' ? 'bg-amber-500/90' : 'bg-blue-500/90'
                      }`}>
                        {template.category === 'professional' ? tx('Professional', 'מקצועי') :
                         template.category === 'creative' ? tx('Creative', 'יצירתי') :
                         template.category === 'industry' ? tx('Industry', 'תעשייתי') : tx('Basic', 'בסיסי')}
                      </div>

                      {/* Preview Button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewTemplate(template) }}
                        className="absolute top-2 left-2 w-7 h-7 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                        title={tx('Preview', 'תצוגה מקדימה')}
                      >
                        <Eye className="w-3.5 h-3.5 text-slate-700" />
                      </button>
                    </div>

                    {/* Footer */}
                    <div className="p-2.5 flex items-center justify-between border-t border-slate-100">
                      <span className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Star className="w-2.5 h-2.5" />
                        {template.nameHe}
                      </span>
                      <span className="text-[10px] text-slate-400">{template.blocks.length} {tx('blocks', 'בלוקים')}</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* No Results */}
          {filteredBuiltIn.length === 0 && filteredSaved.length === 0 && (
            <div className="text-center py-16">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">{tx('No results found', 'לא נמצאו תוצאות')}</h3>
              <p className="text-slate-400 mt-1">{tx('Try searching for something else', 'נסה לחפש משהו אחר')}</p>
            </div>
          )}
        </main>
      </div>

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onConfirm={() => {
            handleUseTemplate(previewTemplate.blocks)
            setPreviewTemplate(null)
          }}
        />
      )}

      {/* Edit Template Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" dir="ltr">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-accent-500 text-white p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Edit3 className="w-5 h-5" />
                  {tx('Edit Template', 'עריכת תבנית')}
                </h2>
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{tx('Template Name', 'שם התבנית')}</label>
                <input
                  type="text"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{tx('Description', 'תיאור')}</label>
                <textarea
                  value={editingTemplate.description}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{tx('Color', 'צבע')}</label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setEditingTemplate({ ...editingTemplate, color: color.value })}
                      className={`w-9 h-9 rounded-lg ${color.bg} transition-transform hover:scale-105 ${
                        editingTemplate.color === color.value ? 'ring-2 ring-offset-2 ring-slate-500' : ''
                      }`}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 p-4 flex justify-end gap-2 bg-slate-50">
              <button
                onClick={() => setEditingTemplate(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                {tx('Cancel', 'ביטול')}
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 text-sm bg-slate-500 text-white rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {tx('Save', 'שמור')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
